import React from "react";
import { Video, Mic, PhoneOff, Activity, MailCheck, Headphones, Paperclip, Sparkles } from "lucide-react";

/* Mini live preview shown next to the service cards in Step 1 of the wizard. */

const NAVY = "#0F2B5B";
const SKY = "#4FC3F7";

function Frame({ title, children }) {
  return (
    <div
      className="rounded-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
      style={{ border: "0.5px solid #E8EDF5", boxShadow: "0 16px 40px -18px rgba(15,43,91,0.28)", background: "#ffffff" }}
    >
      <div className="flex items-center gap-2 px-3 py-2" style={{ background: "#F8FAFC", borderBottom: "0.5px solid #E8EDF5" }}>
        <span className="w-2 h-2 rounded-full bg-red-300" />
        <span className="w-2 h-2 rounded-full bg-amber-300" />
        <span className="w-2 h-2 rounded-full bg-emerald-300" />
        <span className="ml-2 text-[10px] truncate" style={{ color: "#94A3B8" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function TeleconsMock() {
  return (
    <Frame title="votre-plateforme.fr/teleconsultation">
      <div className="p-3" style={{ background: NAVY }}>
        <div className="grid grid-cols-2 gap-2">
          {[["Dr Martin", "#1E3A6E"], ["Patient", "#16305F"]].map(([name, bg]) => (
            <div key={name} className="rounded-lg h-20 relative grid place-items-center" style={{ background: bg }}>
              <div className="w-8 h-8 rounded-full grid place-items-center text-[10px] font-bold text-white" style={{ background: SKY, color: NAVY }}>
                {name[0] === "D" ? "DM" : "PA"}
              </div>
              <span className="absolute bottom-1 left-1.5 text-[8.5px] text-white/70">{name}</span>
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-center gap-2">
          <span className="w-7 h-7 rounded-full grid place-items-center bg-white/10"><Mic size={12} className="text-white" /></span>
          <span className="w-7 h-7 rounded-full grid place-items-center bg-white/10"><Video size={12} className="text-white" /></span>
          <span className="w-7 h-7 rounded-full grid place-items-center bg-red-500"><PhoneOff size={12} className="text-white" /></span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[8.5px] text-white/50 px-1">
          <span>Visio chiffrée E2E · LiveKit</span>
          <span className="text-emerald-400">● Conforme HAS</span>
        </div>
      </div>
    </Frame>
  );
}

function TeleexpMock() {
  return (
    <Frame title="votre-plateforme.fr/teleexpertise">
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <span className="w-6 h-6 rounded-full grid place-items-center shrink-0 text-[9px] font-bold text-white" style={{ background: NAVY }}>MG</span>
          <div className="rounded-lg rounded-tl-none px-2.5 py-1.5 text-[10px]" style={{ background: "#F0F9FF", color: NAVY }}>
            Avis dermato demandé — lésion suspecte, photos jointes.
            <div className="mt-1 inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#ffffff", border: "0.5px solid #BAE6FD", color: "#0369A1" }}>
              <Paperclip size={9} /> photo_lesion.jpg
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2 flex-row-reverse">
          <span className="w-6 h-6 rounded-full grid place-items-center shrink-0 text-[9px] font-bold" style={{ background: SKY, color: NAVY }}>SP</span>
          <div className="rounded-lg rounded-tr-none px-2.5 py-1.5 text-[10px] text-white" style={{ background: NAVY }}>
            Avis rendu sous 48h — biopsie recommandée. Compte-rendu envoyé via MSSanté.
          </div>
        </div>
        <div className="text-[8.5px] text-center" style={{ color: "#94A3B8" }}>Échange asynchrone sécurisé · MSSanté</div>
      </div>
    </Frame>
  );
}

function TelesurvMock() {
  const bars = [42, 58, 50, 66, 72, 61, 78, 84, 70, 88];
  return (
    <Frame title="votre-plateforme.fr/telesurveillance">
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold" style={{ color: NAVY }}>Mme Dupont · SpO₂ / Tension</span>
          <span className="text-[8.5px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold animate-pulse">⚠ Alerte seuil</span>
        </div>
        <div className="flex items-end gap-1 h-16">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 rounded-t transition-all" style={{ height: `${h}%`, background: i === bars.length - 1 ? "#F59E0B" : SKY, opacity: 0.5 + i * 0.05 }} />
          ))}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
          {[["SpO₂", "94%"], ["Tension", "14/9"], ["Pouls", "82"]].map(([k, v]) => (
            <div key={k} className="rounded-md py-1" style={{ background: "#F8FAFC", border: "0.5px solid #E8EDF5" }}>
              <div className="text-[8px]" style={{ color: "#94A3B8" }}>{k}</div>
              <div className="text-[11px] font-bold" style={{ color: NAVY }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

function WebmailMock() {
  const mails = [
    ["Dr Bernard — CHU", "CR consultation cardio", true],
    ["Labo Bio86", "Résultats analyses — Mme R.", true],
    ["CPTS Vienne", "Réunion coordination jeudi", false],
  ];
  return (
    <Frame title="votre-plateforme.fr/mssante">
      <div className="p-2.5">
        <div className="flex items-center gap-1.5 mb-2 px-1">
          <MailCheck size={12} style={{ color: SKY }} />
          <span className="text-[10px] font-semibold" style={{ color: NAVY }}>Boîte MSSanté intégrée</span>
          <span className="ml-auto text-[8.5px] px-1.5 rounded-full text-white font-semibold" style={{ background: SKY, color: NAVY }}>2 non lus</span>
        </div>
        <div className="space-y-1">
          {mails.map(([from, subj, unread]) => (
            <div key={subj} className="flex items-center gap-2 rounded-md px-2 py-1.5" style={{ background: unread ? "#F0F9FF" : "#ffffff", border: "0.5px solid #E8EDF5" }}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${unread ? "bg-sky-400" : "bg-slate-200"}`} />
              <div className="min-w-0">
                <div className="text-[9.5px] font-semibold truncate" style={{ color: NAVY }}>{from}</div>
                <div className="text-[9px] truncate" style={{ color: "#64748B" }}>{subj}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

const MOCKS = {
  telecons: { comp: TeleconsMock, icon: Video, label: "Téléconsultation" },
  teleexp: { comp: TeleexpMock, icon: Headphones, label: "Téléexpertise" },
  telesurv: { comp: TelesurvMock, icon: Activity, label: "Télésurveillance" },
  webmail: { comp: WebmailMock, icon: MailCheck, label: "Webmail MSSanté" },
};

export default function StudioMiniPreview({ services, focusService }) {
  const active = focusService && services.includes(focusService) ? focusService : services[services.length - 1];

  if (!active) {
    return (
      <div
        className="rounded-xl p-6 text-center grid place-items-center min-h-[220px]"
        style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1" }}
        data-testid="mini-preview-empty"
      >
        <div>
          <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#94A3B8" }} />
          <p className="text-[12.5px] leading-relaxed" style={{ color: "#64748B" }}>
            Sélectionnez un service pour voir un aperçu live du module s&apos;allumer ici.
          </p>
        </div>
      </div>
    );
  }

  const { comp: Mock, icon: Icon, label } = MOCKS[active];
  return (
    <div data-testid="mini-preview" className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD" }}>
          <Icon size={14} style={{ color: NAVY }} />
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: SKY }}>Aperçu live</div>
          <div className="text-[13px] font-semibold leading-tight" style={{ color: NAVY }}>{label}</div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] font-semibold text-emerald-600">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En direct
        </span>
      </div>
      <Mock key={active} />
      {services.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {services.filter((s) => MOCKS[s]).map((s) => {
            const m = MOCKS[s];
            const on = s === active;
            return (
              <span
                key={s}
                className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={on ? { background: NAVY, color: "#fff" } : { background: "#F1F5F9", color: "#64748B" }}
              >
                {m.label}
              </span>
            );
          })}
        </div>
      )}
      <p className="text-[11px] leading-relaxed" style={{ color: "#94A3B8" }}>
        Ceci est un aperçu simplifié — la sandbox complète vous attend à la fin du parcours.
      </p>
    </div>
  );
}
